import { Liquid } from "liquidjs";
import _ from "lodash";
import { LiquidVariable } from "../services/bedrock";

const engine = new Liquid();
export const LIQUID_PLACEHOLDER_REGEX = /{{.*?}}|{%-?.*?-?%}/;

export const renderLiquid = (template: string | null | undefined, variables: object): string | null => {
  if (!template?.trim()) return null;
  try {
    return engine.parseAndRenderSync(template, variables).trim() || null;
  } catch {
    return null;
  }
};

export type LiquidPlaceholderGroups = { [group: string]: { [displayName: string]: string } };

const placeholderEntries = (liquidVariables: LiquidVariable[], chainedKeys: string[]): [string, string][] =>
  liquidVariables.flatMap((liquidVariable): [string, string][] => {
    if (liquidVariable.dataType === "liquid_drop") {
      return placeholderEntries(liquidVariable.value as LiquidVariable[], [...chainedKeys, liquidVariable.name]).map(
        ([subKey, subValue]) => [_.startCase(liquidVariable.name) + " " + subKey, subValue]
      );
    }
    return [[_.startCase(liquidVariable.name), [...chainedKeys, liquidVariable.name].join(".")]];
  });

export const groupLiquidPlaceholders = (liquidVariables: LiquidVariable[]): LiquidPlaceholderGroups =>
  Object.fromEntries(
    liquidVariables
      .filter(liquidVariable => liquidVariable.dataType === "liquid_drop")
      .map(liquidVariable => [
        liquidVariable.name,
        Object.fromEntries(placeholderEntries(liquidVariable.value as LiquidVariable[], [liquidVariable.name])),
      ])
  );

export const convertLiquidVariablesToObject = (liquidVariables: LiquidVariable[]): { [key: string]: any } => {
  const obj: { [key: string]: any } = {};
  liquidVariables.forEach(variable => {
    if (variable.dataType === "liquid_drop") {
      obj[variable.name] = convertLiquidVariablesToObject(variable.value as LiquidVariable[]);
    } else {
      obj[variable.name] = variable.value;
    }
  });
  return obj;
};

export const renderLiquidInObject = (obj: any, liquidVariables: any): any => {
  if (!LIQUID_PLACEHOLDER_REGEX.test(JSON.stringify(obj))) {
    return obj;
  }
  if (typeof obj === "string") {
    try {
      return engine.parseAndRenderSync(obj, liquidVariables);
    } catch (e) {
      return obj;
    }
  } else if (Array.isArray(obj)) {
    return obj.map(item => renderLiquidInObject(item, liquidVariables));
  } else if (obj && typeof obj === "object") {
    const entries = Object.entries(obj).map(([key, value]) => {
      const renderedValue = renderLiquidInObject(value, liquidVariables);
      return [key, renderedValue];
    });
    return Object.fromEntries(entries);
  } else {
    return obj;
  }
};
